/**
 * Sprint V98.2 · Webinar-Regpage Long-Form (Do 17.09.2026, Andreas via CH-00).
 * Route: /webinar/:id/anmeldung
 *
 * Copy 1:1 aus Andreas' Regpage-Copy-Brief:
 *   Section 1 · HERO (H1, Sub, Sound-On, Video, Formular, Micro-Reassurance)
 *   Section 2 · SCHMERZ (3 Pain-Points)
 *   Section 3 · FÜR WEN
 *   Section 4 · WAS DU MITNIMMST + ABLAUF (3 Steps)
 *   Section 9 · FINALER CTA + FOOTER
 *
 * Wortlaut-Regel: „E-Mail eintragen / Zugang erhalten / kostenfrei" —
 * kein „Registrieren / Anmelden / Abo".
 *
 * Tracking bleibt unangetastet:
 *   - Meta Pixel ViewContent + CAPI-Twin `/track/regpage-view`
 *   - fbq trackCustom WebinarCTAClicked + CAPI `/track/cta-click`
 *   - GA4 webinar_regpage_view / webinar_cta_click
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CalendarClock,
  CheckCircle2,
  Loader2,
  VideoOff,
  Volume2,
  Sparkles,
  Eye,
  Users,
  MessageCircleHeart,
  ArrowRight,
  ShieldCheck,
  Clock,
  Gift,
  UserCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const _once = (() => {
  const seen = new Set();
  return (key) => { if (seen.has(key)) return false; seen.add(key); return true; };
})();

function readSession() {
  try {
    let sid = localStorage.getItem("f4s_session_id");
    if (!sid) {
      sid = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem("f4s_session_id", sid);
    }
    return sid;
  } catch { return null; }
}

function fmtDateDe(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const fmt = new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin",
      weekday: "short",
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
    const p = fmt.formatToParts(d).reduce((a, x) => ({ ...a, [x.type]: x.value }), {});
    return `${p.weekday}, ${p.day}.${p.month}.${p.year} · ${p.hour}:${p.minute} Uhr`;
  } catch { return iso; }
}

/** Wochentag lang (z. B. "Dienstag") — nutzt Europe/Berlin. */
function fmtWeekdayLong(iso) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin", weekday: "long",
    }).format(new Date(iso));
  } catch { return ""; }
}

/** Uhrzeit als "19 Uhr" wenn volle Stunde, sonst "19:30 Uhr". */
function fmtTimeLabel(iso) {
  if (!iso) return "";
  try {
    const fmt = new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
    const p = fmt.formatToParts(new Date(iso)).reduce((a, x) => ({ ...a, [x.type]: x.value }), {});
    const hh = String(parseInt(p.hour, 10));
    return p.minute === "00" ? `${hh} Uhr` : `${hh}:${p.minute} Uhr`;
  } catch { return ""; }
}

/** Tageszeit-Suffix aus Uhrzeit ("Abend"/"Morgen"/…) — kombiniert mit Wochentag: "Dienstagabend". */
function fmtWeekdayDaypart(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const wd = new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin", weekday: "long",
    }).format(d);
    const hourStr = new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin", hour: "2-digit", hour12: false,
    }).formatToParts(d).find((p) => p.type === "hour")?.value;
    const h = parseInt(hourStr || "0", 10);
    let daypart = "abend";
    if (h < 11) daypart = "morgen";
    else if (h < 14) daypart = "mittag";
    else if (h < 17) daypart = "nachmittag";
    else if (h < 22) daypart = "abend";
    else daypart = " Nacht"; // Notlösung; wird für Webinare i. d. R. nicht auftreten
    return `${wd}${daypart}`;
  } catch { return ""; }
}

/** Wiederverwendbares Registrierungs-Formular. Bei mehreren Instanzen
 *  auf der Seite scrollt der zweite CTA nach oben zum Hero-Formular
 *  (weniger Duplicate-Feld-Logik, keine Doppelt-Submits). */
function RegistrationForm({
  emailId, firstNameId, gdprId,
  submitting, submitError,
  email, firstName, gdpr,
  onEmailChange, onFirstNameChange, onGdprChange,
  onSubmit,
  testIdPrefix = "webinar",
  buttonLabel = "Zugang erhalten",
}) {
  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 sm:p-8 shadow-lg"
      data-testid={`${testIdPrefix}-form`}
    >
      <label className="block">
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Vorname <span className="text-slate-400 font-normal">(optional)</span>
        </span>
        <input
          id={firstNameId}
          type="text"
          value={firstName}
          onChange={(e) => onFirstNameChange(e.target.value)}
          autoComplete="given-name"
          placeholder="Wie soll ich dich ansprechen?"
          className="mt-2 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#4563BB]"
          data-testid={`${testIdPrefix}-input-firstname`}
        />
      </label>
      <label className="block mt-4">
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          E-Mail-Adresse
        </span>
        <input
          id={emailId}
          type="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          required
          autoComplete="email"
          placeholder="deine@email.de"
          className="mt-2 w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-[#4563BB]"
          data-testid={`${testIdPrefix}-input-email`}
        />
      </label>
      <label className="mt-4 flex items-start gap-3 text-xs text-slate-600 dark:text-slate-400">
        <input
          id={gdprId}
          type="checkbox"
          checked={gdpr}
          onChange={(e) => onGdprChange(e.target.checked)}
          className="mt-1"
          data-testid={`${testIdPrefix}-input-gdpr`}
        />
        <span>
          Ich möchte den Zugang zum Webinar per E-Mail erhalten. Ich kann
          mich jederzeit wieder austragen.
        </span>
      </label>
      {submitError && (
        <div
          className="mt-4 rounded-lg bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 px-4 py-3 text-sm text-rose-800 dark:text-rose-200"
          data-testid={`${testIdPrefix}-submit-error`}
        >
          {submitError}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting || !email || !gdpr}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold px-6 py-4 text-base sm:text-lg transition-colors shadow-lg"
        data-testid={`${testIdPrefix}-cta-submit`}
      >
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
        {submitting ? "Wird bestätigt…" : buttonLabel}
      </button>
      <p className="mt-3 text-center text-xs text-slate-500 dark:text-slate-400">
        Du erhältst eine Bestätigung. Du kannst jederzeit abbestellen.
      </p>
    </form>
  );
}

/** Sprint V100+ (18.09.2026) — One-Click-Registrierung für eingeloggte User.
 *  Zeigt einen kompakten Card statt des vollen Formulars: Anrede + GDPR-Checkbox
 *  + Big-CTA. Email/Vorname werden aus /auth/me übernommen und NICHT als Input
 *  gerendert (weniger Hürde, weniger Datenschutz-Risiko). GDPR-Opt-in bleibt
 *  explizit erforderlich (rechtliche Vorgabe). */
function OneClickRegistrationCard({
  user, gdpr, onGdprChange, onSubmit,
  submitting, submitError,
  gdprId, testIdPrefix = "webinar-oneclick",
  buttonLabel = "Ja, ich bin dabei",
}) {
  const name = (user?.first_name || "").trim();
  const email = user?.email || "";
  return (
    <form
      onSubmit={onSubmit}
      className="rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 sm:p-8 shadow-lg"
      data-testid={`${testIdPrefix}-form`}
    >
      <div
        className="flex items-start gap-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 px-4 py-3 mb-5"
        data-testid={`${testIdPrefix}-identity-card`}
      >
        <UserCheck className="w-5 h-5 mt-0.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 dark:text-white">
            {name ? `Hallo ${name}!` : "Du bist eingeloggt."}
          </p>
          <p
            className="text-xs text-slate-600 dark:text-slate-300 truncate"
            data-testid={`${testIdPrefix}-identity-email`}
          >
            {email}
          </p>
        </div>
      </div>
      <label className="flex items-start gap-3 text-xs text-slate-600 dark:text-slate-400">
        <input
          id={gdprId}
          type="checkbox"
          checked={gdpr}
          onChange={(e) => onGdprChange(e.target.checked)}
          className="mt-1"
          data-testid={`${testIdPrefix}-input-gdpr`}
        />
        <span>
          Ich möchte den Zugang zum Webinar per E-Mail erhalten. Ich kann
          mich jederzeit wieder austragen.
        </span>
      </label>
      {submitError && (
        <div
          className="mt-4 rounded-lg bg-rose-50 dark:bg-rose-900/30 border border-rose-200 dark:border-rose-800 px-4 py-3 text-sm text-rose-800 dark:text-rose-200"
          data-testid={`${testIdPrefix}-submit-error`}
        >
          {submitError}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting || !gdpr}
        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold px-6 py-4 text-base sm:text-lg transition-colors shadow-lg"
        data-testid={`${testIdPrefix}-cta-submit`}
      >
        {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
        {submitting ? "Wird bestätigt…" : buttonLabel}
      </button>
      <p className="mt-3 text-center text-xs text-slate-500 dark:text-slate-400">
        Du erhältst eine Bestätigung. Du kannst jederzeit abbestellen.
      </p>
    </form>
  );
}

export default function WebinarRegpage() {
  const { id = "1" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isLoggedIn = !!(user && user.email);
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  // DSGVO: Checkbox MUSS standardmäßig deaktiviert sein (Opt-in erforderlich)
  const [gdpr, setGdpr] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const viewFiredRef = useRef(false);

  // Sprint V100+ (18.09.2026) — Prefill aus Auth-Context.
  // Sobald useAuth den User hydriert hat, seeden wir email + first_name
  // ins Formular. Datensparsam: kein URL-Param, kein PII-Leak in Browser-History.
  useEffect(() => {
    if (!user) return;
    if (user.email && !email) setEmail(user.email);
    if (user.first_name && !firstName) setFirstName(user.first_name);
  }, [user, email, firstName]);

  // Sprint V102.g (Mo 22.09.2026) — Prefill via URL-Query (KlickTipp-Link).
  // Erwartet `?t=<email>&fn=<vorname>` (analog zu /schneller-zugang und
  // /api/auth/kt-login, damit KT-Redakteure denselben Platzhalter-Satz nutzen).
  // Kein Auto-Submit — User klickt noch bewusst auf "Anmelden".
  // Wir strippen die Query nach dem Lesen, damit die Adresse in der Browser-
  // History nicht die E-Mail enthaelt (Datensparsamkeit).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const tParam = (params.get("t") || "").trim();
    const fnParam = (params.get("fn") || "").trim();
    let changed = false;
    if (tParam && tParam.includes("@") && !tParam.startsWith("%")) {
      setEmail((prev) => prev || tParam.toLowerCase());
      changed = true;
    }
    if (fnParam && !fnParam.startsWith("%")) {
      setFirstName((prev) => prev || fnParam);
      changed = true;
    }
    if (changed) {
      // PII aus URL entfernen, utm-Params behalten (Analytics).
      params.delete("t");
      params.delete("fn");
      const rest = params.toString();
      const clean = window.location.pathname + (rest ? `?${rest}` : "");
      window.history.replaceState({}, "", clean);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get(`/webinar/${id}`)
      .then((r) => { if (!cancelled) setConfig(r.data); })
      .catch((e) => {
        if (!cancelled) setLoadError(e?.response?.data?.detail || "Termin nicht gefunden");
      });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (!config || viewFiredRef.current) return;
    if (!_once(`regpage_view:${id}`)) { viewFiredRef.current = true; return; }
    viewFiredRef.current = true;
    const eventId = `wrpv_${id}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    try {
      if (typeof window.fbq === "function") {
        window.fbq("track", "ViewContent", {
          content_name: "webinar_regpage",
          content_ids: [`webinar_${id}`],
        }, { eventID: eventId });
      }
    } catch { /* silent */ }
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", "webinar_regpage_view", { webinar_id: id });
      }
    } catch { /* silent */ }
    api.post(`/webinar/${id}/track/regpage-view`, {
      session_id: readSession(),
      original_page_url: window.location.href,
    }).catch(() => {});
  }, [config, id]);

  const fireCtaTracking = () => {
    const eventId = `wcta_${id}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    try {
      if (typeof window.fbq === "function") {
        window.fbq("trackCustom", "WebinarCTAClicked", {
          content_name: "webinar_regpage", webinar_id: id,
        }, { eventID: eventId });
      }
    } catch { /* silent */ }
    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", "webinar_cta_click", { webinar_id: id });
      }
    } catch { /* silent */ }
    api.post(`/webinar/${id}/track/cta-click`, {
      email, session_id: readSession(),
      original_page_url: window.location.href,
    }).catch(() => {});
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!email || !gdpr || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    fireCtaTracking();
    try {
      let fokustyp = null;
      try { fokustyp = localStorage.getItem("f4s_primary_fokustyp") || null; } catch { /* silent */ }
      const { data } = await api.post(`/webinar/${id}/register`, {
        email, first_name: firstName, fokustyp,
        session_id: readSession(),
        original_page_url: window.location.href,
        gdpr_consent: gdpr, source: "webinar_regpage",
      });
      navigate(`/webinar/${id}/bestaetigung?email=${encodeURIComponent(data.email)}`);
    } catch (err) {
      setSubmitError(
        err?.response?.data?.detail ||
        "Kurzer Aussetzer. Bitte gleich noch einmal probieren."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const scrollToHeroForm = (e) => {
    e?.preventDefault?.();
    const el = document.getElementById("webinar-hero-form-email");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => el.focus(), 500);
    }
  };

  const dateDe = useMemo(() => (config ? fmtDateDe(config.event_datetime) : ""), [config]);
  const weekdayLong = useMemo(() => (config ? fmtWeekdayLong(config.event_datetime) : ""), [config]);
  const timeLabel = useMemo(() => (config ? fmtTimeLabel(config.event_datetime) : ""), [config]);
  const weekdayDaypart = useMemo(() => (config ? fmtWeekdayDaypart(config.event_datetime) : ""), [config]);
  const daysUntil = useMemo(() => {
    if (!config?.event_datetime) return null;
    try {
      const t = new Date(config.event_datetime).getTime();
      if (t < Date.now()) return null;
      const d = Math.max(1, Math.ceil((t - Date.now()) / (1000 * 60 * 60 * 24)));
      return d === 1 ? "1 Tag" : `${d} Tagen`;
    } catch { return null; }
  }, [config]);

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6">
        <div className="max-w-md text-center" data-testid="webinar-load-error">
          <h1 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
            Termin nicht gefunden
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-300">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950"
      data-testid="webinar-regpage"
      data-webinar-id={config.webinar_id}
    >
      {/* Sprint V102.h (22.09.2026) — Banner "Aktuell nicht mehr buchbar".
          Erscheint ganz oben quer, wenn Admin `is_bookable=false` gesetzt hat.
          Termin bleibt sichtbar, Formular wird durch Warteliste-CTA ersetzt. */}
      {config.is_bookable === false && (
        <div
          data-testid="webinar-not-bookable-banner"
          className="w-full bg-red-600 text-white text-center py-3 px-4 font-bold text-sm sm:text-base shadow-sm"
        >
          Aktuell nicht mehr buchbar — dieser Termin nimmt keine Anmeldungen mehr an.
        </div>
      )}
      {/* ================= SECTION 1 · HERO ================= */}
      <section
        className="relative overflow-hidden max-w-6xl mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-14"
        data-testid="webinar-hero"
      >
        {/* Dezenter 4-Felder-Matrix-Hintergrund (Eule · Katze · Biene · Hund) */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 grid grid-cols-2 grid-rows-2 gap-0 select-none"
          data-testid="hero-matrix-bg"
        >
          {/*
           * Kanonische Fokustypen-Reihenfolge (2×2):
           *   oben-links Katze · oben-rechts Eule
           *   unten-links Hund · unten-rechts Biene
           * CSS-Grid füllt zeilenweise: [1]=OL, [2]=OR, [3]=UL, [4]=UR
           */}
          {[
            { key: "katze", src: "/icons/icon-katze.png", pos: "items-start justify-start p-6 sm:p-12" },
            { key: "eule", src: "/icons/icon-eule.png", pos: "items-start justify-end p-6 sm:p-12" },
            { key: "hund", src: "/icons/icon-hund.png", pos: "items-end justify-start p-6 sm:p-12" },
            { key: "biene", src: "/icons/icon-biene.png", pos: "items-end justify-end p-6 sm:p-12" },
          ].map((t) => (
            <div key={t.key} className={`flex ${t.pos}`}>
              <img
                src={t.src}
                alt=""
                aria-hidden="true"
                className="w-24 sm:w-36 lg:w-48 h-auto opacity-[0.06] dark:opacity-[0.08]"
                loading="lazy"
              />
            </div>
          ))}
          {/* Mittiges Kreuz — sehr dezente Trennlinien wie in der Matrix */}
          <div className="absolute inset-0">
            <div className="absolute top-1/2 left-0 right-0 h-px bg-slate-900/[0.03] dark:bg-white/[0.04]" />
            <div className="absolute top-0 bottom-0 left-1/2 w-px bg-slate-900/[0.03] dark:bg-white/[0.04]" />
          </div>
        </div>

        {/* Volle Breite: Eyebrow + Headline + Sub + Datum + Sound-Hinweis */}
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <p
            className="text-[11px] sm:text-xs font-bold uppercase tracking-widest text-[#F3651D]"
            data-testid="hero-eyebrow"
          >
            Kostenfreies Live-Webinar
            {daysUntil ? <> &middot; Live in {daysUntil}</> : null}
          </p>
          <h1
            className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 dark:text-white leading-[1.1]"
            data-testid="webinar-title"
          >
            Erkenne dich.<br />
            Verstehe andere.<br />
            <span className="text-[#F3651D]">Gestalte deine Wirkung.</span>
          </h1>
          <p
            className="mt-5 text-base sm:text-lg text-slate-700 dark:text-slate-300 leading-relaxed max-w-2xl mx-auto"
            data-testid="webinar-subtitle"
          >
            Im Webinar lernst du Schritt für Schritt, wie du die vier
            Fokustypen liest — bei dir, beim anderen, in der Brücke
            dazwischen.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <div
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-4 py-2 text-sm font-semibold"
              data-testid="webinar-date"
            >
              <CalendarClock className="w-4 h-4" />
              {dateDe}
            </div>
            <div
              className="inline-flex items-center gap-2 rounded-full bg-[#F3651D]/10 text-[#F3651D] px-4 py-2 text-sm font-semibold"
              data-testid="webinar-sound-hint"
            >
              <Volume2 className="w-4 h-4" />
              Ton an. Wir reden kurz mit dir.
            </div>
          </div>
        </div>

        {/* Zwei Spalten: Video links, Anmeldebox rechts — auf gleicher Höhe */}
        <div className="relative z-10 mt-10 sm:mt-14 grid lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
          {/* Left: Video */}
          <div className="flex flex-col" data-testid="hero-video-col">
            <p className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-4">
              Schau dir kurz dieses Video an.
            </p>
            <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-900 shadow-xl">
              {config.has_video ? (
                <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
                  <iframe
                    src={`https://player.vimeo.com/video/${config.video_id}?playsinline=1&dnt=1&title=0&byline=0&portrait=0${config.video_hash ? `&h=${config.video_hash}` : ""}`}
                    title={config.title}
                    className="absolute inset-0 w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; encrypted-media; clipboard-write"
                    allowFullScreen
                    referrerPolicy="strict-origin-when-cross-origin"
                    data-testid="webinar-video-iframe"
                  />
                </div>
              ) : (
                <div
                  className="aspect-video flex flex-col items-center justify-center gap-3 text-slate-100 p-8"
                  data-testid="webinar-video-placeholder"
                >
                  <VideoOff className="w-12 h-12 text-slate-400" />
                  <p className="text-sm text-slate-300 text-center max-w-md">
                    Video wird geladen &hellip;
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Right: Anmeldebox — Headline auf gleicher Höhe wie Video-Headline */}
          <div className="flex flex-col" data-testid="hero-form-col">
            {config.is_bookable === false ? (
              /* Sprint V102.h — Ehrlicher CTA statt "Warteliste"-Etikette (23.09.2026):
                 Klick fuehrt auf den QuickCheck (Fokustypen-Test in 90 Sek.).
                 Wenn Admin `waitlist_url` gesetzt hat, wird der bevorzugt. */
              <div
                data-testid="webinar-waitlist-card"
                className="rounded-2xl border-2 border-red-200 dark:border-red-900/60 bg-red-50/60 dark:bg-red-950/30 p-6 sm:p-7"
              >
                <p className="text-lg sm:text-xl font-extrabold text-red-900 dark:text-red-200 mb-2">
                  Aktuell nicht mehr buchbar.
                </p>
                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                  Bis der naechste Termin steht, kannst du bereits <strong>in 90 Sekunden deinen Fokustypen</strong> ermitteln — kostenlos. Sobald der neue Termin online ist, bekommst du ueber unseren Verteiler automatisch Bescheid.
                </p>
                <a
                  href={config.waitlist_url || "/quickcheck"}
                  data-testid="webinar-waitlist-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F3651D] hover:opacity-90 text-white font-bold px-5 py-3 text-sm sm:text-base transition-opacity w-full sm:w-auto"
                >
                  <CheckCircle2 className="w-5 h-5" />
                  Jetzt QuickCheck starten
                </a>
                <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                  Dauert unter 2 Minuten. Danach siehst du direkt, welcher der vier Fokustypen dich am staerksten praegt — und wie du damit besser kommunizierst.
                </p>
              </div>
            ) : (
              <>
                <p className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white mb-4">
                  {isLoggedIn
                    ? "Bestätige kurz — dann bist du dabei."
                    : "Trag dich ein. Du erhältst den Zugang sofort."}
                </p>
                {isLoggedIn ? (
                  <OneClickRegistrationCard
                    user={user}
                    gdpr={gdpr}
                    onGdprChange={setGdpr}
                    onSubmit={handleSubmit}
                    submitting={submitting}
                    submitError={submitError}
                    gdprId="webinar-hero-form-gdpr"
                    testIdPrefix="webinar"
                    buttonLabel="Ja, ich bin dabei"
                  />
                ) : (
                  <RegistrationForm
                    emailId="webinar-hero-form-email"
                    firstNameId="webinar-hero-form-firstname"
                    gdprId="webinar-hero-form-gdpr"
                    submitting={submitting} submitError={submitError}
                    email={email} firstName={firstName} gdpr={gdpr}
                    onEmailChange={setEmail} onFirstNameChange={setFirstName} onGdprChange={setGdpr}
                    onSubmit={handleSubmit}
                    testIdPrefix="webinar"
                    buttonLabel="Zugang erhalten"
                  />
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* ================= SECTION 2 · SCHMERZ ================= */}
      <section
        className="bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800"
        data-testid="section-schmerz"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white text-center">
            Für dich, wenn&hellip;
          </h2>
          <div className="mt-10 space-y-4">
            {[
              "Du weißt, was du sagen willst. Beim anderen kommt was anderes an.",
              "Du reagierst schnell — und weißt es eigentlich besser.",
              "Du führst dieselben Gespräche immer wieder. Partner, Team, Kinder.",
            ].map((pain, i) => (
              <div
                key={i}
                data-testid={`pain-${i + 1}`}
                className="flex items-start gap-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-5"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#F3651D]/10 text-[#F3651D] flex items-center justify-center font-bold text-lg">
                  {i + 1}
                </div>
                <p className="text-base sm:text-lg text-slate-800 dark:text-slate-100 leading-relaxed">
                  {pain}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= SECTION 3 · FÜR WEN ================= */}
      <section
        className="bg-slate-50 dark:bg-slate-950"
        data-testid="section-fuer-wen"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-[#F3651D]">
            Für wen
          </p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white">
            Für alle, die verstehen wollen, warum manche Gespräche sofort
            kippen — und was sie im nächsten Moment anders machen können.
          </h2>
          <p className="mt-5 text-base text-slate-600 dark:text-slate-400 leading-relaxed">
            Egal, ob du deinen Fokustyp schon kennst oder gerade erst erfahren
            hast. Das Webinar bringt dich vom Ergebnis in deinen Alltag.
          </p>
        </div>
      </section>

      {/* ================= SECTION 4 · ABLAUF ================= */}
      <section
        className="bg-white dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800"
        data-testid="section-ablauf"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white text-center">
            So läuft das Webinar
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <Eye className="w-6 h-6" />,
                step: "1",
                title: "Erkenne dich.",
                body: "Wo stehst du gerade? Welcher Reiz löst bei dir welche Reaktion aus?",
              },
              {
                icon: <Users className="w-6 h-6" />,
                step: "2",
                title: "Verstehe andere.",
                body: "Wie liest du dein Gegenüber in 30 Sekunden? Was braucht sie oder er gerade?",
              },
              {
                icon: <MessageCircleHeart className="w-6 h-6" />,
                step: "3",
                title: "Gestalte deine Wirkung.",
                body: "Welcher Satz funktioniert in welcher Situation? Wie baust du die Brücke?",
              },
            ].map((s) => (
              <div
                key={s.step}
                className="rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-6"
                data-testid={`ablauf-step-${s.step}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[#F3651D] text-white flex items-center justify-center shadow">
                    {s.icon}
                  </div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[#F3651D]">
                    Schritt {s.step}
                  </div>
                </div>
                <p className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
                  {s.title}
                </p>
                <p className="mt-2 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
                  {s.body}
                </p>
              </div>
            ))}
          </div>

          {/* Zusatz-Bullet */}
          <div
            className="mt-10 rounded-2xl bg-gradient-to-r from-[#F3651D]/10 to-[#4563BB]/10 dark:from-[#F3651D]/20 dark:to-[#4563BB]/20 border border-[#F3651D]/30 p-6 flex items-start gap-4"
            data-testid="ablauf-outcome"
          >
            <Sparkles className="w-6 h-6 text-[#F3651D] flex-shrink-0 mt-0.5" />
            <p className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white leading-relaxed">
              Du gehst mit einem konkreten Werkzeug raus, das am nächsten Tag wirkt.
            </p>
          </div>

          {/* Mid-page CTA */}
          <div className="mt-10 text-center">
            <button
              type="button"
              onClick={scrollToHeroForm}
              className="inline-flex items-center gap-2 rounded-xl bg-[#F3651D] hover:bg-[#d95415] text-white font-bold px-8 py-4 text-base sm:text-lg transition-colors shadow-lg"
              data-testid="mid-cta-to-form"
            >
              Zugang erhalten
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* ================= SECTION 9 · FINALER CTA ================= */}
      <section
        className="bg-slate-900 dark:bg-black text-white"
        data-testid="section-final-cta"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-center">
            Sei {weekdayLong || "am Termin"}{timeLabel ? ` um ${timeLabel}` : ""} live dabei.
          </h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { icon: <Clock className="w-5 h-5" />, label: "60 Minuten" },
              { icon: <Gift className="w-5 h-5" />, label: "kostenfrei" },
              { icon: <Sparkles className="w-5 h-5" />, label: "du gehst mit einem Werkzeug raus" },
              { icon: <VideoOff className="w-5 h-5" />, label: "Aufzeichnung im Pro-Plan" },
            ].map((b, i) => (
              <div
                key={i}
                className="rounded-xl bg-slate-800 border border-slate-700 p-4 text-center"
                data-testid={`final-bullet-${i + 1}`}
              >
                <div className="w-9 h-9 mx-auto rounded-full bg-[#F3651D]/20 text-[#F3651D] flex items-center justify-center">
                  {b.icon}
                </div>
                <p className="mt-2 text-sm font-semibold">{b.label}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 max-w-lg mx-auto">
            {config.is_bookable === false ? (
              /* Sprint V102.h — Ehrlicher CTA am Seiten-Ende. */
              <div
                data-testid="webinar-waitlist-card-final"
                className="rounded-2xl border-2 border-red-300 bg-red-50/10 p-6 text-center"
              >
                <p className="text-lg font-extrabold text-white mb-2">
                  Aktuell nicht mehr buchbar.
                </p>
                <p className="text-sm text-slate-300 mb-4 leading-relaxed">
                  Bis der neue Termin steht: bestimme jetzt in 90 Sekunden deinen Fokustypen — kostenlos.
                </p>
                <a
                  href={config.waitlist_url || "/quickcheck"}
                  data-testid="webinar-waitlist-cta-final"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#F3651D] hover:opacity-90 text-white font-bold px-6 py-3 text-base transition-opacity"
                >
                  <CheckCircle2 className="w-5 h-5" />
                  Jetzt QuickCheck starten
                </a>
              </div>
            ) : isLoggedIn ? (
              <OneClickRegistrationCard
                user={user}
                gdpr={gdpr}
                onGdprChange={setGdpr}
                onSubmit={handleSubmit}
                submitting={submitting}
                submitError={submitError}
                gdprId="webinar-final-form-gdpr"
                testIdPrefix="webinar-final"
                buttonLabel="Ja, ich bin dabei"
              />
            ) : (
              <RegistrationForm
                emailId="webinar-final-form-email"
                firstNameId="webinar-final-form-firstname"
                gdprId="webinar-final-form-gdpr"
                submitting={submitting} submitError={submitError}
                email={email} firstName={firstName} gdpr={gdpr}
                onEmailChange={setEmail} onFirstNameChange={setFirstName} onGdprChange={setGdpr}
                onSubmit={handleSubmit}
                testIdPrefix="webinar-final"
                buttonLabel="Zugang erhalten"
              />
            )}
          </div>

          <div className="mt-8 flex items-center justify-center gap-2 text-xs text-slate-400">
            <ShieldCheck className="w-4 h-4" />
            Du kannst jederzeit abbestellen.
          </div>

          {/* Footer-Links */}
          <div
            className="mt-12 pt-8 border-t border-slate-700 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-slate-400"
            data-testid="webinar-footer-links"
          >
            <a href="/impressum" className="hover:text-white transition-colors">Impressum</a>
            <span>&middot;</span>
            <a href="/datenschutz" className="hover:text-white transition-colors">
              Datenschutz
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
